import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";

import { Disclose, EmptyState, Loading, Permission, Refusal, kernel } from "@lopie/kernel-ui";

import Game from "./Game.jsx";

const PAGE = 200;
const MAX_PAGES = 5;

/**
 * Math Runner: a side scroller whose gates are sums equal to payment amounts.
 *
 * What it reads comes from this app's own row in GET /apps, and where each
 * type lives and which field is its money comes from GET /schema: the game
 * uses the declared type that has a minor-units amount, so nothing about the
 * kernel's shape is hardcoded here.
 */
export default function App({ token, claims, onSignOut }) {
  const get = useMemo(() => kernel(token), [token]);
  const [state, setState] = useState({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const [apps, schema] = await Promise.all([get("/apps"), get("/schema")]);
      const self = (apps.apps || []).find((a) => a.is_self);
      const declared = (self?.reads || []).map((g) => g.type);
      const types = schema.types || [];

      // The first declared type that carries a minor-units amount.
      let chosen = null;
      for (const name of declared) {
        const t = types.find((x) => x.type === name);
        const amt = t?.fields?.find((f) => f.minor_units && f.currency_field);
        if (t?.collection && amt) {
          const date = t.fields.find((f) => f.type === "timestamp" && f.required)
            || t.fields.find((f) => f.type === "timestamp");
          chosen = {
            type: t.type,
            collection: t.collection,
            fields: { amount: amt.name, currency: amt.currency_field, date: date?.name },
          };
          break;
        }
      }
      if (!chosen) {
        setState({ status: "nothing-declared", declared });
        return;
      }

      let items = [];
      let cursor;
      let body;
      let more = false;
      try {
        for (let i = 0; i < MAX_PAGES; i++) {
          body = await get(`/${chosen.collection}`, cursor ? { limit: PAGE, cursor } : { limit: PAGE });
          items = items.concat(body.items || []);
          more = !!body.has_more;
          if (!more) break;
          cursor = body.cursor;
        }
      } catch (e) {
        setState({ status: "refused", failure: e, chosen });
        return;
      }
      const usable = items.filter((p) => Number.isFinite(Number(p.fields?.[chosen.fields.amount])));
      setState({
        status: "ready",
        chosen,
        items: usable,
        truncated: more,
        permitted: body?.permitted_tags || [],
        declaredTags: body?.declared_tags || null,
      });
    } catch (e) {
      setState({ status: "failed", failure: e });
    }
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  const header = (
    <Stack
      direction="row"
      spacing={2}
      sx={{ alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}
    >
      <Typography variant="h1">Math Runner</Typography>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">{claims?.username || ""}</Typography>
        <Typography
          component="button"
          variant="body2"
          onClick={onSignOut}
          sx={{ background: "none", border: 0, cursor: "pointer", color: "primary.main" }}
        >
          Sign out
        </Typography>
      </Stack>
    </Stack>
  );

  const permission = (
    <Permission
      gate={
        "Governed by this app's reviewed manifest: the payments in this game are "
        + "that ceiling intersected with your own groups. The app only reads; it "
        + "never changes a payment."
      }
      groups={claims?.["cognito:groups"] || []}
      effective={state.status === "ready" ? { permitted: state.permitted, declared: state.declaredTags } : undefined}
    />
  );

  let body;
  if (state.status === "loading") {
    body = <Loading label="Reading your payments from the kernel" minHeight="40vh" />;
  } else if (state.status === "failed" || state.status === "refused") {
    body = <Refusal failure={state.failure} actor="Math Runner" onRetry={load} />;
  } else if (state.status === "nothing-declared") {
    body = (
      <EmptyState title="This app declares no type with money amounts">
        <Typography variant="body2">
          Its approved manifest reads {state.declared.length ? state.declared.join(", ") : "nothing"}, and
          none of those has an amount to build sums from. That is the app&apos;s declaration, not
          something about you.
        </Typography>
      </EmptyState>
    );
  } else if (state.items.length === 0) {
    body = (
      <EmptyState title="No payments you may see, so there is nothing to play with" tagged={(state.permitted || []).join(", ")}>
        <Typography variant="body2">
          The kernel answered and returned no {state.chosen.type} records for you. Either there are
          none yet, or none that your access lets you see. The app was allowed to ask, so this is
          not a refusal.
        </Typography>
      </EmptyState>
    );
  } else {
    body = (
      <>
        <Game payments={state.items} fields={state.chosen.fields} />
        <Disclose label="Where the numbers come from">
          <Stack spacing={1} sx={{ maxWidth: "62ch" }}>
            <Typography variant="body2" color="text.secondary">
              Every correct answer is the {state.chosen.fields.amount} of one {state.chosen.type} record
              you can see, read live from the kernel when this page loaded ({state.items.length}
              {state.truncated ? `, the first ${PAGE * MAX_PAGES} only` : ""}). The other choices are
              other payments&apos; amounts or made-up decoys, and the game says which after you answer.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Scores are kept only in this browser tab and are not saved anywhere.
            </Typography>
          </Stack>
        </Disclose>
      </>
    );
  }

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      {header}
      {permission}
      <Box sx={{ minWidth: 0 }}>{body}</Box>
    </Stack>
  );
}
